import {
  AdapterDateFns,
  FormControl,
  FormControlLabel,
  InputLabel,
  LocalizationProvider,
  MenuItem,
  PlatformDialog,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
  pxToRem,
  StaticDatePicker,
  Grid2,
} from "@platform/style";
import React from "react";
import { startOfTomorrow } from "date-fns";
import { ActionEnum, ResolutionReason } from "../models/ReasonsAndResolutions";
import { fetchWithToken } from "@platform/core";
import { WORK_MANAGEMENT_ADMIN_BFF_API_BASE } from "../platform-workitems";
import { reportError } from "../utils/errors";

interface PopUpDialogBoxProps {
  optional?: boolean;
  typeOfAction: string;
  open: boolean;
  title: string;
  maxDate?: Date;
  onClose: () => void;
  onSubmit: (resolutionCode: string, comment: string, selectedDate?: Date | null) => void;
}

const enum CalenderViewEnum {
  YEAR = "year",
  DAY = "day",
}

export const PopUpDialogBox = ({
  optional = false,
  typeOfAction,
  open,
  title,
  onSubmit,
  onClose,
  maxDate,
}: PopUpDialogBoxProps) => {
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(null);
  const [skipFollowupDate, setSkipFollowupDate] = React.useState(false);
  const [reasonAndResolutionList, setReasonAndResolutionList] = React.useState<ResolutionReason[]>(
    []
  );
  const [selectedReasonCode, setSelectedReasonCode] = React.useState<string>("");
  const [comment, setComment] = React.useState<string | undefined>();
  const [inputLabel, setInputLabel] = React.useState<string | undefined>();
  const [calenderView, setCalenderView] = React.useState<string>(CalenderViewEnum.DAY);
  const minDate = startOfTomorrow();

  React.useEffect(() => {
    if (typeOfAction.replace(" ", "") in ActionEnum) {
      switch (typeOfAction) {
        case ActionEnum.ONHOLD:
          setInputLabel("Reason Type");
          break;
        case ActionEnum.COMPLETE:
        case ActionEnum.CANCEL:
          setInputLabel("Resolution Code");
          break;
      }
      (async () => {
        await fetchWithToken(
          `${WORK_MANAGEMENT_ADMIN_BFF_API_BASE}/resolution-reasons?action=${encodeURIComponent(
            typeOfAction
          )
            .valueOf()
            .replace(" ", "+")}`,
          {
            method: "GET",
          }
        )
          .then((response) => {
            if (!response.ok) {
              throw new Error(JSON.stringify(response));
            }
            return response.json();
          })
          .then((result) => {
            setReasonAndResolutionList(result ? result : []);
          })
          .catch((error) => {
            reportError(error.message);
          });
      })();
    }
  }, [typeOfAction]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSkipFollowupDate(event.target.checked);
  };

  const handleSubmit = () => {
    onSubmit(selectedReasonCode, comment || "", selectedDate);
  };

  const handleItemChange = (event: SelectChangeEvent) => {
    setSelectedReasonCode(event.target.value);
  };

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.trim();
    setComment(value === "" ? undefined : value);
  };

  const MenuProps = {
    PaperProps: {
      sx: {
        width: "440px",
        p: 2,
        borderRadius: pxToRem(4),
        overflowY: "auto",
        maxHeight: "calc(100vh - 540px)",
      },
    },
  };

  return (
    <PlatformDialog
      open={open}
      title={title}
      onClose={onClose}
      onSubmit={handleSubmit}
      isSubmitDisabled={
        (!skipFollowupDate && selectedDate === null && typeOfAction == ActionEnum.ONHOLD) ||
        !(selectedReasonCode && selectedReasonCode.length > 0)
      }
      maxWidth={490}
    >
      <Grid2 container direction="column" justifyContent="space-around" alignItems="center">
        <Grid2 sx={{ mt: "20px", mb: "20px" }}>
          <FormControl defaultValue="" required fullWidth={true}>
            <InputLabel required={true} shrink id="select-reasoncode-label">
              {inputLabel}
            </InputLabel>
            <Select
              variant="outlined"
              name={"Reason Type"}
              notched={true}
              data-testid={"select-reasoncode-testid"}
              labelId="select-reasoncode-label"
              id="select-reasoncode-id"
              value={selectedReasonCode}
              label={inputLabel}
              displayEmpty
              onChange={handleItemChange}
              size={"small"}
              MenuProps={MenuProps}
              sx={{ textAlign: "left", width: "440px", height: "45px" }}
            >
              {reasonAndResolutionList.map((reason) => (
                <MenuItem
                  sx={{ whiteSpace: "normal", wordWrap: "break-all" }}
                  value={reason.ResolutionReasonCode}
                >
                  {reason.ResolutionReason}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid2>

        {(!skipFollowupDate || !optional) && typeOfAction == ActionEnum.ONHOLD && (
          <Grid2 sx={{ mb: calenderView == CalenderViewEnum.YEAR ? "30px" : "0px" }}>
            <FormControl aria-labelledby={!optional ? "demo-radio-buttons-group-label" : undefined}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <StaticDatePicker
                  onViewChange={(view) => {
                    view == CalenderViewEnum.YEAR
                      ? setCalenderView(CalenderViewEnum.YEAR)
                      : setCalenderView(CalenderViewEnum.DAY);
                  }}
                  displayStaticWrapperAs="desktop"
                  openTo="day"
                  minDate={minDate}
                  referenceDate={minDate}
                  maxDate={maxDate}
                  value={selectedDate}
                  onChange={(newValue) => {
                    setSelectedDate(newValue);
                  }}
                  slotProps={
                    {
                      textField: {
                        variant: "outlined",
                      },
                    } as any
                  }
                />
              </LocalizationProvider>
            </FormControl>
          </Grid2>
        )}
        {optional && typeOfAction == ActionEnum.ONHOLD && (
          <Grid2 sx={{ mt: skipFollowupDate ? "20px" : "-30px" }}>
            <FormControl>
              <FormControlLabel
                control={<Switch checked={skipFollowupDate} onChange={handleChange} />}
                label="Put on hold without follow up date"
              />
            </FormControl>
          </Grid2>
        )}
        {typeOfAction && (
          <Grid2
            sx={{
              mt: typeOfAction == ActionEnum.ONHOLD ? (optional ? "20px" : "-30px") : "10px",
              mb: "5px",
              size: "small",
            }}
          >
            <InputLabel id="comment-label-id">Comment</InputLabel>
            <TextField
              id="comment-textfield-id"
              multiline
              rows={2}
              slotProps={{
                htmlInput: {
                  maxLength: 500,
                },
              }}
              sx={{
                width: "440px",
              }}
              helperText={
                comment ? `Character Count: ${500 - comment.length}` : `Character Count: 500`
              }
              onChange={handleCommentChange}
              name="comment-textfield-name"
              FormHelperTextProps={{
                sx: {
                  display: "flex",
                  justifyContent: "flex-end",
                  fontSize: "13px",
                },
              }}
            />
          </Grid2>
        )}
      </Grid2>
    </PlatformDialog>
  );
};
